import { API } from '@aws-amplify/api'
import {
  getUsersList,
  deleteUser,
  getUsersByGroupId,
  getUsersListByGroupId,
  updateUsersDataFetchInProgress,
  addUser,
  editUser,
  getsessionStatus,
  tokenActiveStatus,
} from '../actions'
import { WOMA_API_GATEWAY_NAME } from '../../constants/aws'
import { ADD_USER_ERROR, ADD_USER_SUCCESS, EDIT_USER_SUCCESS, EDIT_USER_ERROR } from './../actionConstants'

interface UserInputs {
  // eslint-disable-next-line @typescript-eslint/ban-types
  formObj?: object
  userName: string
  firstName: string
  lastName: string
  phoneNumber: string
  stateLicenses: string[]
  processCodes: string[]
  isQcPerformed: boolean
  isHandheldAllowed: boolean
  isPriorityCasesAllowed: boolean
  isStateOverrideAllowed: boolean
  userType: string
  userStatus: boolean
}

interface UserSentObj {
  userName: string
  firstName: string
  lastName: string
  phoneNumber: string
  stateLicenses: string[]
  processCodes: string[]
  isQcPerformed: boolean
  isHandheldAllowed: boolean
  isPriorityCasesAllowed: boolean
  isStateOverrideAllowed: boolean
  userType: string
  userStatus: boolean
}

interface GetUsersByGroupId {
  body?: string
  groupId?: string
}
interface UsersDelete {
  id: string | null | string[]
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const fetchUsersData = () => async (dispatch) => {
  const apiName = 'user-management/users'

  try {
    const options = {
      headers: {},
      response: false,
    }
    dispatch(updateUsersDataFetchInProgress(true))
    const result = await API.get(WOMA_API_GATEWAY_NAME, `/${apiName}`, options)

    if (result?.data) {
      dispatch(getUsersList({ payload: result }))
    }
  } catch (err) {
    dispatch(getUsersList({ payload: 'GET_USERS_DATA_ERROR' }))
    console.log('Cases Error', err)
  } finally {
    dispatch(updateUsersDataFetchInProgress(false))
  }
}

export const fetchUsersByGroupId =
  ({ groupId }: GetUsersByGroupId) =>
  async (dispatch) => {
    const apiName = 'user-management/users/groups'
    try {
      const options = {
        headers: {},
        response: false,
      }
      const result = await API.get(WOMA_API_GATEWAY_NAME, `/${apiName}/${groupId}`, options)

      if (result) {
        dispatch(getUsersByGroupId({ payload: result, groupId }))
      }
    } catch (err) {
      console.log('Cases Error', err)
    }
  }

export const fetchUsersListByGroupId =
  ({ groupId }: GetUsersByGroupId) =>
  async (dispatch) => {
    const apiName = 'user-management/users/groups'
    try {
      const options = {
        headers: {},
        response: false,
      }
      const result = await API.get(WOMA_API_GATEWAY_NAME, `/${apiName}/${groupId}`, options)

      if (result) {
        dispatch(getUsersListByGroupId({ payload: result, groupId }))
      }
    } catch (err) {
      console.log('Cases Error', err)
    }
  }

export const deleteUserData =
  ({ id }: UsersDelete) =>
  async (dispatch) => {
    if (!id || id instanceof Array) return

    const apiName = 'user-management/users'

    try {
      const options = {
        headers: {},
        /**
         * response: true => return the entire Axios response object instead
         * of only response.data
         * */
        response: false,
        queryStringParameters: {
          userId: id,
        },
      }

      const result = await API.del(WOMA_API_GATEWAY_NAME, `/${apiName}/${id}`, options)
      if (result?.message === 'SUCCESS') {
        dispatch(deleteUser({ payload: result.data, userId: id }))
        dispatch(fetchUsersData())
      }
    } catch (err) {
      console.log('Cases Error', err)
    }
  }

export const addUserData =
  ({ payload, id }) =>
  async (dispatch) => {
    const apiName = 'user-management/users'
    let res = false
    try {
      const options = {
        headers: {},
        response: false,
        body: payload,
      }
      const result = await API.post(WOMA_API_GATEWAY_NAME, `/${apiName}/${id}`, options)
      res = true
      dispatch(fetchUsersData())
    } catch (err) {
      res = false
      console.log('Cases Error', err)
    } finally {
      dispatch(addUser({ payload: {}, action: res ? ADD_USER_SUCCESS : ADD_USER_ERROR }))
    }
  }

export const editUserData =
  ({ payload, id }) =>
  async (dispatch) => {
    const apiName = 'user-management/users'
    let res = false
    try {
      const options = {
        headers: {},
        response: false,
        body: payload,
      }
      const result = await API.put(WOMA_API_GATEWAY_NAME, `/${apiName}/${id}`, options)
      res = true
      dispatch(fetchUsersData())
    } catch (err) {
      res = false
      console.log('Cases Error', err)
    } finally {
      console.log('res-finally')
      dispatch(editUser({ payload: {}, action: res ? EDIT_USER_SUCCESS : EDIT_USER_ERROR }))
    }
  }

export const deactivateUserData =
  ({ userId, deactivateUserId }) =>
  async (dispatch) => {
    const apiName = 'user-management/users/user-status'
    try {
      const options = {
        headers: {},
        response: false,
        queryStringParameters: {
          id: deactivateUserId,
          newstatus: 0,
        },
      }
      const result = await API.put(WOMA_API_GATEWAY_NAME, `/${apiName}/${userId}`, options)
      dispatch(fetchUsersData())
    } catch (err) {
      console.log('Cases Error', err)
    }
  }

export const activateUserData =
  ({ userId, activateUserId }) =>
  async (dispatch) => {
    const apiName = 'user-management/users/user-status'
    try {
      const options = {
        headers: {},
        response: false,
        queryStringParameters: {
          id: activateUserId,
          newstatus: 1,
        },
      }
      const result = await API.put(WOMA_API_GATEWAY_NAME, `/${apiName}/${userId}`, options)
      dispatch(fetchUsersData())
    } catch (err) {
      console.log('Cases Error', err)
    }
  }

export const tokenActive =
  ({ userId }) =>
  async (dispatch) => {
    const apiName = `user-management/session-token`
    try {
      const options = {
        headers: {},
        response: false,
        queryStringParameters: {},
      }
      const result = await API.get(WOMA_API_GATEWAY_NAME, `/${apiName}/${userId}`, options)
      await dispatch(tokenActiveStatus({ payload: result }))
    } catch (err) {
      console.log('Cases Error', err)
    }
  }

export const userSessionToken =
  ({ userId, password }) =>
  async (dispatch) => {
    let res = false
    const apiName = `user-management/session-token`
    let sessionError
    try {
      const options = {
        headers: {},
        response: false,
        queryStringParameters: {},
        body: { password: password },
      }
      const result = await API.post(WOMA_API_GATEWAY_NAME, `/${apiName}/${userId}`, options)
      res = true
    } catch (err) {
      res = false
      sessionError = err
    } finally {
      dispatch(
        getsessionStatus({
          action: res ? 'SESSION_SUCCESS' : 'SESSION_ERROR',
          payload: res ? 'success' : sessionError?.message,
        }),
      )
    }
  }

export const clearSessionToken = () => async (dispatch) => {
  await dispatch(getsessionStatus({ action: 'SESSION_SUCCESS', payload: '' }))
}
